import { z } from 'zod';
import { chatTriggerSourceSchema } from './chat';
import { ResourceType } from '../resource/resource.types';
import type {
  ApiSchemaClass,
  ApiSchemaDefinition,
  ApiSchemaMapperClass,
  ApiSchemaMapperDefinition,
} from './types';
import {
  emptyObjectResponseSchema,
  resourceActivateShareLinkResponseSchema,
  resourceFavoritesResponseSchema,
  resourceListResponseSchema,
} from './common-responses';

const resourceTypeSchema = z.nativeEnum(ResourceType);

const sharePermissionLevelSchema = z.enum(['view', 'edit']);
const resourceShareModeSchema = z.enum(['access', 'copy']);
const resourceShareGrantTargetTypeSchema = z.enum(['user', 'org']);
const resourceShareLinkAudienceSchema = z.enum(['org', 'anyone']);
const grantedPermissionLevelSchema = z.enum(['view', 'edit', 'admin']);

/**
 * A resource named by type and id. Shared by every payload that points at one
 * rather than describing it — share targets, the favorites reorder list.
 */
const resourceRefSchema = z.object({
  type: resourceTypeSchema,
  id: z.string().uuid(),
});

const resourceSharePostActivationStepSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('navigate'),
    path: z.string().min(1),
  }),
  z.object({
    t: z.literal('chat'),
    prompt: z.string(),
  }),
]);

const resourceSharePostActivationSequenceSchema = z.object({
  steps: z.array(resourceSharePostActivationStepSchema),
});

const emptyBodySchema = z.object({});

/** Shared path params: POST/GET /resource/:type/:id/... */
export const resourceTypeIdParamsSchema = z.object({
  type: resourceTypeSchema.describe('Resource type'),
  id: z.string().uuid().describe('Resource UUID'),
});

export type ResourceTypeIdParams = z.infer<typeof resourceTypeIdParamsSchema>;

/** Path params: GET /resource/:type */
export const resourceTypeParamsSchema = z.object({
  type: resourceTypeSchema.describe('Resource type to list or fetch'),
});

export type ResourceTypeParams = z.infer<typeof resourceTypeParamsSchema>;

// ---------------------------------------------------------------------------
// POST /resource/share-links
// ---------------------------------------------------------------------------

export const resourceCreateShareLinkBodySchema = z.object({
  targets: z
    .array(resourceRefSchema)
    .min(1)
    .max(50)
    .describe('Resources included in the share link'),
  shareMode: resourceShareModeSchema.describe(
    'Whether recipients get access or a copy'
  ),
  grantTargetType: resourceShareGrantTargetTypeSchema
    .optional()
    .describe('Who may activate the link'),
  permission: sharePermissionLevelSchema
    .optional()
    .describe('Permission granted to recipients'),
  resourceFilter: z.record(z.string(), z.unknown()).optional(),
  postActivationSequence: resourceSharePostActivationSequenceSchema.optional(),
  expiresInDays: z
    .number()
    .int()
    .min(1)
    .max(30)
    .optional()
    .describe('Link expiry in days'),
  audience: resourceShareLinkAudienceSchema
    .optional()
    .describe(
      'Who may open the link: `org` for members of your organization, `anyone` for anyone signed in to Coco. Default: `org` for an access link, `anyone` for a copy link'
    ),
});

export type ResourceCreateShareLinkBody = z.infer<
  typeof resourceCreateShareLinkBodySchema
>;

export const resourceCreateShareLinkResponseSchema = z.object({
  link: z.unknown(),
  token: z.string(),
  url: z.string(),
});

export type ResourceCreateShareLinkResponse = z.infer<
  typeof resourceCreateShareLinkResponseSchema
>;

export class ResourceCreateShareLinkLegacySchema
  implements ApiSchemaDefinition
{
  static readonly version = 'resource-create-share-link-legacy' as const;

  readonly name = ResourceCreateShareLinkLegacySchema.version;
  readonly schema = resourceCreateShareLinkBodySchema;
  readonly response = resourceCreateShareLinkResponseSchema;
}

export class ResourceCreateShareLinkV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'resource-create-share-link-2026-06-01' as const;

  readonly name = ResourceCreateShareLinkV20260601Schema.version;
  readonly schema = resourceCreateShareLinkBodySchema;
  readonly response = resourceCreateShareLinkResponseSchema;

  static mappers(): ApiSchemaMapperClass[] {
    return [ResourceCreateShareLinkLegacyToV20260601Mapper];
  }
}

export class ResourceCreateShareLinkLegacyToV20260601Mapper
  implements ApiSchemaMapperDefinition
{
  readonly fromVersion = ResourceCreateShareLinkLegacySchema.version;
  readonly toVersion = ResourceCreateShareLinkV20260601Schema.version;
}

// ---------------------------------------------------------------------------
// POST /resource/share-links/activate
// ---------------------------------------------------------------------------

export const resourceActivateShareLinkBodySchema = z.object({
  token: z.string().min(8),
});

export type ResourceActivateShareLinkBody = z.infer<
  typeof resourceActivateShareLinkBodySchema
>;

export class ResourceActivateShareLinkLegacySchema
  implements ApiSchemaDefinition
{
  static readonly version = 'resource-activate-share-link-legacy' as const;

  readonly name = ResourceActivateShareLinkLegacySchema.version;
  readonly schema = resourceActivateShareLinkBodySchema;
  readonly response = resourceActivateShareLinkResponseSchema;
}

export class ResourceActivateShareLinkV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'resource-activate-share-link-2026-06-01' as const;

  readonly name = ResourceActivateShareLinkV20260601Schema.version;
  readonly schema = resourceActivateShareLinkBodySchema;
  readonly response = resourceActivateShareLinkResponseSchema;

  static mappers(): ApiSchemaMapperClass[] {
    return [ResourceActivateShareLinkLegacyToV20260601Mapper];
  }
}

export class ResourceActivateShareLinkLegacyToV20260601Mapper
  implements ApiSchemaMapperDefinition
{
  readonly fromVersion = ResourceActivateShareLinkLegacySchema.version;
  readonly toVersion = ResourceActivateShareLinkV20260601Schema.version;
}

// ---------------------------------------------------------------------------
// POST /resource/share-links/:type/:id/organization
// ---------------------------------------------------------------------------

export const resourceSetSharedWithOrganizationBodySchema = z.object({
  permission: sharePermissionLevelSchema.describe(
    'Organization-wide permission to grant on the resource'
  ),
});

export type ResourceSetSharedWithOrganizationBody = z.infer<
  typeof resourceSetSharedWithOrganizationBodySchema
>;

export class ResourceSetSharedWithOrganizationLegacySchema
  implements ApiSchemaDefinition
{
  static readonly version = 'resource-set-shared-org-legacy' as const;

  readonly name = ResourceSetSharedWithOrganizationLegacySchema.version;
  readonly schema = resourceSetSharedWithOrganizationBodySchema;
  readonly params = resourceTypeIdParamsSchema;
  readonly response = emptyObjectResponseSchema;
}

export class ResourceSetSharedWithOrganizationV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'resource-set-shared-org-2026-06-01' as const;

  readonly name = ResourceSetSharedWithOrganizationV20260601Schema.version;
  readonly schema = resourceSetSharedWithOrganizationBodySchema;
  readonly params = resourceTypeIdParamsSchema;
  readonly response = emptyObjectResponseSchema;

  static mappers(): ApiSchemaMapperClass[] {
    return [ResourceSetSharedWithOrganizationLegacyToV20260601Mapper];
  }
}

export class ResourceSetSharedWithOrganizationLegacyToV20260601Mapper
  implements ApiSchemaMapperDefinition
{
  readonly fromVersion = ResourceSetSharedWithOrganizationLegacySchema.version;
  readonly toVersion = ResourceSetSharedWithOrganizationV20260601Schema.version;
}

// ---------------------------------------------------------------------------
// GET /resource/:type (query params; type is a path param)
// ---------------------------------------------------------------------------

const folderScopeSchema = z.enum(['private', 'shared']);
const sortDirSchema = z.enum(['asc', 'desc']);

const looseQueryNumberSchema = z.union([z.string(), z.number()]).optional();
const looseQueryBooleanSchema = z.union([z.string(), z.boolean()]).optional();

/**
 * Query boolean that honours an explicit `false`.
 *
 * `z.coerce.boolean()` runs `Boolean(value)`, so the string `'false'` arrives as
 * `true`. That is invisible for opt-in flags like `unreadOnly` (callers just omit
 * them), but wrong for anything whose meaningful value is `false`.
 */
const explicitQueryBooleanSchema = z.preprocess((value) => {
  if (value === undefined || value === '') {
    return undefined;
  }
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return value;
}, z.boolean().optional());

const optionalQueryEnumArraySchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.preprocess((value) => {
    if (value === undefined || value === '') {
      return undefined;
    }
    if (Array.isArray(value)) {
      return value;
    }
    return [value];
  }, z.array(itemSchema).optional());

export const resourceListQueryLegacySchema = z.object({
  limit: looseQueryNumberSchema,
  offset: looseQueryNumberSchema,
  folderScope: folderScopeSchema.optional(),
  search: z.string().optional(),
  sort: z.string().optional(),
  sortDir: sortDirSchema.optional(),
  triggerSources: optionalQueryEnumArraySchema(chatTriggerSourceSchema),
  workflowScheduledAgentOnly: looseQueryBooleanSchema,
  unreadOnly: looseQueryBooleanSchema,
  agentId: z.string().uuid().optional(),
  triggerKind: z
    .enum(['cron', 'webhook', 'event', 'dataset', 'none'])
    .optional(),
});

export type ResourceListQueryLegacy = z.infer<
  typeof resourceListQueryLegacySchema
>;

export const resourceListQueryV20260601Schema = z.object({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .describe('Maximum number of resources to return'),
  offset: z.coerce
    .number()
    .int()
    .min(0)
    .optional()
    .describe('Number of resources to skip'),
  folderScope: folderScopeSchema
    .optional()
    .describe('Filter by private or shared folder scope'),
  ownership: z
    .enum(['mine', 'shared'])
    .optional()
    .describe(
      'Ownership lens: `mine` = owned by the actor; `shared` = inbound user grants the actor does not own (org-wide excluded)'
    ),
  search: z
    .string()
    .optional()
    .describe('Search term matched against resource names'),
  sort: z.string().optional().describe('Field to sort by'),
  sortDir: sortDirSchema.optional().describe('Sort direction'),
  triggerSources: optionalQueryEnumArraySchema(
    chatTriggerSourceSchema
  ).describe('When set, only return chats with one of these trigger sources'),
  workflowScheduledAgentOnly: z.coerce
    .boolean()
    .optional()
    .describe('Only return workflow scheduled agents'),
  unreadOnly: z.coerce
    .boolean()
    .optional()
    .describe('Only return unread resources'),
  unfiledOnly: z.coerce
    .boolean()
    .optional()
    .describe(
      'When listing chats, only return chats with no folder placement visible to the viewer (the sidebar Unfiled sections). Combine with `folderScope` to bucket by private vs shared.'
    ),
  agentId: z
    .string()
    .uuid()
    .optional()
    .describe('When listing chats, only return chats linked to this agent'),
  includeTotal: explicitQueryBooleanSchema.describe(
    'Defaults to true. When listing chats, set false to skip the exact count over every matching chat — `total` then reports the number of items in this page. Counting is a full pass over the matched set, so pass false whenever you only render a page and never show a total.'
  ),
  triggerKind: z
    .enum(['cron', 'webhook', 'event', 'dataset', 'none'])
    .optional()
    .describe(
      'Filter by trigger kind. When listing workflow triggers, `cron`/`webhook`/`event`/`dataset` select that kind. When listing workflows, `none` returns only workflows with no trigger bound (standalone).'
    ),
});

export type ResourceListQueryV20260601 = z.infer<
  typeof resourceListQueryV20260601Schema
>;

const parseOptionalQueryInt = (value: unknown): number | undefined => {
  if (value === undefined || value === '') {
    return undefined;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }
  return parsed;
};

const clampResourceListLimit = (
  value: number | undefined
): number | undefined => {
  if (value === undefined) {
    return undefined;
  }
  return Math.min(Math.max(value, 1), 100);
};

const parseOptionalQueryBool = (value: unknown): boolean | undefined => {
  if (value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return undefined;
};

export class ResourceListLegacySchema implements ApiSchemaDefinition {
  static readonly version = 'resource-list-legacy' as const;

  readonly name = ResourceListLegacySchema.version;
  readonly schema = emptyBodySchema;
  readonly params = resourceTypeParamsSchema;
  readonly query = resourceListQueryLegacySchema;
  readonly response = resourceListResponseSchema;
}

export class ResourceListV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'resource-list-2026-06-01' as const;

  readonly name = ResourceListV20260601Schema.version;
  readonly schema = emptyBodySchema;
  readonly params = resourceTypeParamsSchema;
  readonly query = resourceListQueryV20260601Schema;
  readonly response = resourceListResponseSchema;

  static mappers(): ApiSchemaMapperClass[] {
    return [ResourceListLegacyToV20260601Mapper];
  }
}

export class ResourceListLegacyToV20260601Mapper
  implements ApiSchemaMapperDefinition
{
  readonly fromVersion = ResourceListLegacySchema.version;
  readonly toVersion = ResourceListV20260601Schema.version;

  queryParams(query: Record<string, unknown>): ResourceListQueryV20260601 {
    return {
      folderScope: query[
        'folderScope'
      ] as ResourceListQueryV20260601['folderScope'],
      ownership: query['ownership'] as ResourceListQueryV20260601['ownership'],
      search: typeof query['search'] === 'string' ? query['search'] : undefined,
      sort: typeof query['sort'] === 'string' ? query['sort'] : undefined,
      sortDir: query['sortDir'] as ResourceListQueryV20260601['sortDir'],
      limit: clampResourceListLimit(parseOptionalQueryInt(query['limit'])),
      offset: parseOptionalQueryInt(query['offset']),
      triggerSources: (() => {
        const raw = query['triggerSources'];
        if (raw === undefined || raw === '') {
          return undefined;
        }
        const values = Array.isArray(raw) ? raw : [raw];
        const parsed = z.array(chatTriggerSourceSchema).safeParse(values);
        return parsed.success ? parsed.data : undefined;
      })(),
      workflowScheduledAgentOnly: parseOptionalQueryBool(
        query['workflowScheduledAgentOnly']
      ),
      unreadOnly: parseOptionalQueryBool(query['unreadOnly']),
      unfiledOnly: parseOptionalQueryBool(query['unfiledOnly']),
      agentId:
        typeof query['agentId'] === 'string' && query['agentId'] !== ''
          ? query['agentId']
          : undefined,
    };
  }
}

// ---------------------------------------------------------------------------
// POST /resource/:type/:id/emoji
// ---------------------------------------------------------------------------

export const resourceSetEmojiBodySchema = z.object({
  emoji: z.string().nullable(),
});

export type ResourceSetEmojiBody = z.infer<typeof resourceSetEmojiBodySchema>;

export class ResourceSetEmojiLegacySchema implements ApiSchemaDefinition {
  static readonly version = 'resource-set-emoji-legacy' as const;

  readonly name = ResourceSetEmojiLegacySchema.version;
  readonly schema = resourceSetEmojiBodySchema;
  readonly params = resourceTypeIdParamsSchema;
  readonly response = emptyObjectResponseSchema;
}

export class ResourceSetEmojiV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'resource-set-emoji-2026-06-01' as const;

  readonly name = ResourceSetEmojiV20260601Schema.version;
  readonly schema = resourceSetEmojiBodySchema;
  readonly params = resourceTypeIdParamsSchema;
  readonly response = emptyObjectResponseSchema;

  static mappers(): ApiSchemaMapperClass[] {
    return [ResourceSetEmojiLegacyToV20260601Mapper];
  }
}

export class ResourceSetEmojiLegacyToV20260601Mapper
  implements ApiSchemaMapperDefinition
{
  readonly fromVersion = ResourceSetEmojiLegacySchema.version;
  readonly toVersion = ResourceSetEmojiV20260601Schema.version;
}

// ---------------------------------------------------------------------------
// Favorites
// ---------------------------------------------------------------------------

/**
 * Favorites are per-user, so these carry no query at all: the actor is the
 * subject, and there is nothing to filter by. There is one version of each,
 * with no legacy predecessor — the feature did not exist before this schema.
 */
export class ResourceListFavoritesV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'resource-list-favorites-2026-06-01' as const;

  readonly name = ResourceListFavoritesV20260601Schema.version;
  readonly schema = emptyBodySchema;
  readonly response = resourceFavoritesResponseSchema;
}

export const resourceSetFavoriteBodySchema = z.object({
  favorite: z
    .boolean()
    .describe('True to star the resource, false to remove the star'),
});

export type ResourceSetFavoriteBody = z.infer<
  typeof resourceSetFavoriteBodySchema
>;

export class ResourceSetFavoriteV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'resource-set-favorite-2026-06-01' as const;

  readonly name = ResourceSetFavoriteV20260601Schema.version;
  readonly schema = resourceSetFavoriteBodySchema;
  readonly params = resourceTypeIdParamsSchema;
  readonly response = emptyObjectResponseSchema;
}

export const resourceReorderFavoritesBodySchema = z.object({
  order: z
    .array(resourceRefSchema)
    .max(500)
    .describe(
      'The full favorites list in its new order. Favorites left out keep their rows and sort after these, so a list starred elsewhere since the client loaded is appended rather than dropped.'
    ),
});

export type ResourceReorderFavoritesBody = z.infer<
  typeof resourceReorderFavoritesBodySchema
>;

export class ResourceReorderFavoritesV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'resource-reorder-favorites-2026-06-01' as const;

  readonly name = ResourceReorderFavoritesV20260601Schema.version;
  readonly schema = resourceReorderFavoritesBodySchema;
  readonly response = emptyObjectResponseSchema;
}

// ---------------------------------------------------------------------------
// Share dialog: who has access, and changing it
//
// GET  /resource/share-links/:type/:id/access
// POST /resource/share-links/:type/:id/people
// POST /resource/share-links/:type/:id/people/:userId/remove
// POST /resource/share-links/:type/:id/organization/remove
// GET  /resource/share-links/:type/:id/links
// GET  /resource/share-links/preview/:token
//
// New in 2026-09-25, so each has one version and no legacy predecessor.
// ---------------------------------------------------------------------------

const accessPersonSchema = z.object({
  userId: z.string().uuid(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  permission: grantedPermissionLevelSchema,
  external: z.boolean(),
  externalOrgName: z.string().nullable(),
});

export const resourceAccessResponseSchema = z.object({
  owner: z.object({
    userId: z.string().uuid(),
    name: z.string().nullable(),
    email: z.string().nullable(),
  }),
  orgAccess: z.object({ permission: grantedPermissionLevelSchema }).nullable(),
  externalOrgs: z.array(
    z.object({
      orgId: z.string().uuid(),
      name: z.string().nullable(),
      permission: grantedPermissionLevelSchema,
    })
  ),
  people: z.array(accessPersonSchema),
  inherited: z.array(
    z.object({
      folderId: z.string().uuid(),
      folderName: z.string(),
      orgPermission: grantedPermissionLevelSchema.nullable(),
      people: z.array(
        z.object({
          userId: z.string().uuid(),
          name: z.string().nullable(),
          email: z.string().nullable(),
          permission: grantedPermissionLevelSchema,
        })
      ),
    })
  ),
  folder: z
    .object({
      folderId: z.string().uuid(),
      folderName: z.string(),
      followsSharing: z.boolean(),
    })
    .nullable()
    .describe(
      'The folder it sits in, and whether its sharing reaches it. Null outside a folder'
    ),
  linked: z
    .array(
      z.object({
        type: resourceTypeSchema,
        id: z.string().uuid(),
        title: z.string(),
      })
    )
    .describe(
      'What sharing this also shares because it uses it, e.g. an agent’s documents'
    ),
  pendingInvites: z
    .array(
      z.object({
        linkId: z.string().uuid(),
        email: z.string(),
        permission: sharePermissionLevelSchema,
        expiresAt: z.coerce.date(),
      })
    )
    .describe('People invited by email who have not opened their link yet'),
  viewer: z.object({
    permission: z.enum(['owner', 'edit', 'view']),
    canManage: z.boolean(),
    hasAccessWithoutOrgShare: z.boolean(),
  }),
});

export type ResourceAccessResponse = z.infer<
  typeof resourceAccessResponseSchema
>;

export class ResourceGetAccessV20260925Schema implements ApiSchemaDefinition {
  static readonly version = 'resource-get-access-2026-09-25' as const;

  readonly name = ResourceGetAccessV20260925Schema.version;
  readonly schema = emptyBodySchema;
  readonly params = resourceTypeIdParamsSchema;
  readonly response = resourceAccessResponseSchema;
}

export const resourceSetUserPermissionBodySchema = z.object({
  userId: z.string().uuid().describe('Org member to add or update'),
  permission: sharePermissionLevelSchema.describe('Level to give them'),
  notify: z
    .object({
      alsoSharing: z
        .number()
        .int()
        .min(0)
        .max(50)
        .describe('Other items shared along with this one, for the wording'),
    })
    .optional()
    .describe(
      'Email the person a link, if this gives them access they did not have'
    ),
});

export const resourceSetUserPermissionResponseSchema = z.object({
  notified: z.boolean().describe('Whether an invite email was sent'),
});

export type ResourceSetUserPermissionBody = z.infer<
  typeof resourceSetUserPermissionBodySchema
>;

export class ResourceSetUserPermissionV20260925Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'resource-set-user-permission-2026-09-25' as const;

  readonly name = ResourceSetUserPermissionV20260925Schema.version;
  readonly schema = resourceSetUserPermissionBodySchema;
  readonly params = resourceTypeIdParamsSchema;
  readonly response = resourceSetUserPermissionResponseSchema;
}

export const resourceInviteByEmailBodySchema = z.object({
  targets: z
    .array(resourceRefSchema)
    .min(1)
    .max(50)
    .describe('Resources to share'),
  email: z
    .string()
    .trim()
    .email()
    .max(320)
    .describe('Address of someone outside the organization'),
  permission: sharePermissionLevelSchema.describe('Level to give them'),
  notify: z
    .boolean()
    .optional()
    .describe(
      'Email them the link (default). When false the link is only returned, for the sharer to send'
    ),
});

export type ResourceInviteByEmailBody = z.infer<
  typeof resourceInviteByEmailBodySchema
>;

export const resourceInviteByEmailResponseSchema = z.object({
  notified: z.boolean().describe('Whether the invite email was sent'),
  token: z.string(),
  url: z
    .string()
    .describe(
      'Path of the invite link, to hand over by hand when the email failed'
    ),
});

/**
 * Invite someone outside the organization by email: Coco sends them a link
 * that only opens for that address.
 */
export class ResourceInviteByEmailV20260928Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'resource-invite-by-email-2026-09-28' as const;

  readonly name = ResourceInviteByEmailV20260928Schema.version;
  readonly schema = resourceInviteByEmailBodySchema;
  readonly response = resourceInviteByEmailResponseSchema;
}

export const resourceUserPermissionParamsSchema =
  resourceTypeIdParamsSchema.extend({
    userId: z.string().uuid().describe('Person whose access to remove'),
  });

export type ResourceUserPermissionParams = z.infer<
  typeof resourceUserPermissionParamsSchema
>;

export class ResourceRemoveUserPermissionV20260925Schema
  implements ApiSchemaDefinition
{
  static readonly version =
    'resource-remove-user-permission-2026-09-25' as const;

  readonly name = ResourceRemoveUserPermissionV20260925Schema.version;
  readonly schema = emptyBodySchema;
  readonly params = resourceUserPermissionParamsSchema;
  readonly response = emptyObjectResponseSchema;
}

export const resourceRemoveSharedWithOrganizationBodySchema = z.object({
  granteeOrgId: z
    .string()
    .uuid()
    .optional()
    .describe(
      'Another organization to stop sharing with (one that opened a link). Default: your own'
    ),
});

export type ResourceRemoveSharedWithOrganizationBody = z.infer<
  typeof resourceRemoveSharedWithOrganizationBodySchema
>;

export const resourceSetFollowsFolderSharingBodySchema = z.object({
  follows: z
    .boolean()
    .describe(
      "false limits it to the people added to it; true lets the folder's sharing reach it again"
    ),
});

export type ResourceSetFollowsFolderSharingBody = z.infer<
  typeof resourceSetFollowsFolderSharingBodySchema
>;

export class ResourceSetFollowsFolderSharingV20260928Schema
  implements ApiSchemaDefinition
{
  static readonly version =
    'resource-set-follows-folder-sharing-2026-09-28' as const;

  readonly name = ResourceSetFollowsFolderSharingV20260928Schema.version;
  readonly schema = resourceSetFollowsFolderSharingBodySchema;
  readonly params = resourceTypeIdParamsSchema;
  readonly response = emptyObjectResponseSchema;
}

export class ResourceRemoveSharedWithOrganizationV20260925Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'resource-remove-shared-org-2026-09-25' as const;

  readonly name = ResourceRemoveSharedWithOrganizationV20260925Schema.version;
  readonly schema = resourceRemoveSharedWithOrganizationBodySchema;
  readonly params = resourceTypeIdParamsSchema;
  readonly response = emptyObjectResponseSchema;
}

export const resourceShareLinkSummarySchema = z.object({
  id: z.string().uuid(),
  shareMode: resourceShareModeSchema,
  permission: sharePermissionLevelSchema.nullable(),
  audience: resourceShareLinkAudienceSchema,
  invitedEmail: z
    .string()
    .nullable()
    .describe('Set when the link is an emailed invite for this address only'),
  targetCount: z.number().int(),
  expiresAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  createdBy: z.object({
    userId: z.string().uuid(),
    name: z.string().nullable(),
    email: z.string().nullable(),
  }),
  openedByCount: z.number().int(),
});

export type ResourceShareLinkSummary = z.infer<
  typeof resourceShareLinkSummarySchema
>;

export class ResourceListActiveShareLinksV20260925Schema
  implements ApiSchemaDefinition
{
  static readonly version =
    'resource-list-active-share-links-2026-09-25' as const;

  readonly name = ResourceListActiveShareLinksV20260925Schema.version;
  readonly schema = emptyBodySchema;
  readonly params = resourceTypeIdParamsSchema;
  readonly response = z.object({
    links: z.array(resourceShareLinkSummarySchema),
  });
}

/** Why a share link can't be opened by the person holding it. */
export const shareLinkProblemSchema = z.enum([
  'not_found',
  'revoked',
  'expired',
  'used',
  'wrong_org',
  'wrong_person',
  'sharer_lost_access',
  'workflow_cross_org',
  'no_targets',
]);

export type ResourceShareLinkProblem = z.infer<typeof shareLinkProblemSchema>;

const sharedBySchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
});

export const resourceShareLinkPreviewResponseSchema = z.discriminatedUnion(
  'status',
  [
    z.object({
      status: z.literal('unavailable'),
      problem: shareLinkProblemSchema,
      sharedBy: sharedBySchema.nullable(),
    }),
    z.object({
      status: z.literal('ready'),
      shareMode: resourceShareModeSchema,
      permission: sharePermissionLevelSchema.nullable(),
      sharedBy: sharedBySchema,
      sourceOrgName: z.string().nullable(),
      sameOrg: z.boolean(),
      targets: z.array(
        z.object({
          type: resourceTypeSchema,
          id: z.string().uuid(),
          title: z.string(),
        })
      ),
      alreadyHasAccess: z.boolean(),
    }),
  ]
);

export type ResourceShareLinkPreviewResponse = z.infer<
  typeof resourceShareLinkPreviewResponseSchema
>;

export const resourceShareLinkTokenParamsSchema = z.object({
  token: z.string().min(8).describe('Share link token from the URL'),
});

export class ResourcePreviewShareLinkV20260925Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'resource-preview-share-link-2026-09-25' as const;

  readonly name = ResourcePreviewShareLinkV20260925Schema.version;
  readonly schema = emptyBodySchema;
  readonly params = resourceShareLinkTokenParamsSchema;
  readonly response = resourceShareLinkPreviewResponseSchema;
}

export const RESOURCE_API_SCHEMAS = [
  ResourceCreateShareLinkLegacySchema,
  ResourceCreateShareLinkV20260601Schema,
  ResourceActivateShareLinkLegacySchema,
  ResourceActivateShareLinkV20260601Schema,
  ResourceSetSharedWithOrganizationLegacySchema,
  ResourceSetSharedWithOrganizationV20260601Schema,
  ResourceListLegacySchema,
  ResourceListV20260601Schema,
  ResourceSetEmojiLegacySchema,
  ResourceSetEmojiV20260601Schema,
  ResourceListFavoritesV20260601Schema,
  ResourceSetFavoriteV20260601Schema,
  ResourceReorderFavoritesV20260601Schema,
  ResourceGetAccessV20260925Schema,
  ResourceSetUserPermissionV20260925Schema,
  ResourceInviteByEmailV20260928Schema,
  ResourceRemoveUserPermissionV20260925Schema,
  ResourceRemoveSharedWithOrganizationV20260925Schema,
  ResourceSetFollowsFolderSharingV20260928Schema,
  ResourceListActiveShareLinksV20260925Schema,
  ResourcePreviewShareLinkV20260925Schema,
] as const satisfies readonly ApiSchemaClass[];
